import React, { Component } from 'react';
import {Link} from 'react-router-dom';

import HttpRunner from '../query';
import EntityIdQuery from '../entityIdQuery';
import urlHandler from '../../../utils/urlHandler';
import perms from '../../../utils/permissions';
import examples from '../../../utils/examples';

class DocV2Entities extends Component {
    constructor(props){
        super(props);

        this.state = {
            dynamicSampleRead: false,
            dynamicSampleSearch: false
        }
    }

    componentDidMount() {
        this.setState({
            dynamicSampleRead: perms.checkEndpoint(urlHandler.API_ENDPOINT_V2_ENTITIES, ['EntityRead']),
            dynamicSampleSearch: perms.checkEndpoint(urlHandler.API_ENDPOINT_V2_ENTITIES, ['EntitySearch'])
        })
    }

    render() {
        return (
            <div className="bucket" id="top">
                <h3 className="heading-1">Entities Lookup</h3>
                <div className="description">
                    The entities GET request provides access to information in the Entity Knowledgebase. The request allows the user to:
                    <div className="bullet-list">
                        <ul className="toc-level list-wrapper">
                            <li className="list-item">Retrieve entities by ID.</li>
                            <li className="list-item">Search entities by name.</li>
                            <li className="list-item">Retrieve basic knowledgebase information including record count and in memory size.</li>
                        </ul>
                    </div>
                </div>

                <h4 className="heading-2">Retrieve By ID</h4>
                <code className="api-definition">
                    <span className="get-badge">GET</span>&nbsp;{urlHandler.API_ENDPOINT_V2_ENTITIES}/{'{'}id{'}'}?{'{'}query parameters{'}'}
                </code>
                <div className="description">
                    Supplying an entity id in the request will provides access to the detailed information in the Entity 
                    Knowledgebase for the given entity id. 
                    
                    For example, the endpoint {urlHandler.API_ENDPOINT_V2_ENTITIES}/40687697921 returns info about Bill Clinton (whose ID happens 
                    to be 40687697921).
                </div>

                <div className="heading-3">URL Parameters</div>
                <div className="description">
                    The following URL parameters are supported for the search request.
                </div>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Notes </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">id</td>
                        <td className="meaning">The Finch identifier.
                        </td>
                        <td className="type">String</td>
                        <td className="type">Required</td>
                    </tr>
                    </tbody>
                </table>

                <div className="heading-3">Query Parameters</div>
                <div className="description">
                    The following query parameters are supported for the lookup request.
                </div>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Notes </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">select</td>
                        <td className="meaning">Comma separated list of fields to return in the response. 
                            The field names must be a valid JSON path for the element.
                        </td>
                        <td className="type">String</td>
                        <td className="type">Optional. Default: All fields<br/>
                            <br/>
                            For arrays, only the array[*] notation, to return all array elements, is supported at this time.
                        </td>
                    </tr>
                    </tbody>
                </table>

                <div className="heading-3">Sample Request</div>
                <div className="example-content host">
                    <pre>
                        GET {urlHandler.API_ENDPOINT_V2_ENTITIES}/358999261185<br/>
                        Accept: application/json<br/>
                        Authorization: Bearer [YOUR-API-KEY]<br/>
                    </pre>
                </div>

                <h4 className="heading-3">Response</h4>
                <div className="description">
                    The lookup request will return a JSON Object. The object <Link to="/documentation/doc_kbattributes">fields</Link> available 
                    will vary by Knowledgebase entity type.
                </div>

                <div className="heading-3">Example: Jeff Bezos</div>
                <HttpRunner query="entities2" 
                    stated="358999261185"
                    response={!this.state.dynamicSampleRead ? JSON.stringify(examples.EXAMPLE_RESPONSE_V2_ENTITIES1) : null}
                />

                <div className="heading-3">Example: Amazon</div>
                <HttpRunner query="entities2" 
                    stated="1454825144322"
                    response={!this.state.dynamicSampleRead ? JSON.stringify(examples.EXAMPLE_RESPONSE_V2_ENTITIES2) : null}
                />

                <div className="heading-3">Example: Washington DC</div>
                <HttpRunner query="entities2" 
                    stated="243859456"
                    response={!this.state.dynamicSampleRead ? JSON.stringify(examples.EXAMPLE_RESPONSE_V2_ENTITIES3) : null}
                />

                {this.state.dynamicSampleRead &&
                    <div>
                        <h4 className="heading-3">Example</h4>
                        <div className="description">
                            Lookup information about any entity by entering their unique entity ID below. 
                            <i>&nbsp;NOTE: Entity ID's are returned in the JSON results on the demo page of this site.</i>
                        </div>
                        <br/>
                        <EntityIdQuery version="v2"/>
                    </div>
                }

                <h4 className="heading-2">Search By Name</h4>
                <code className="api-definition">
                    <span className="get-badge">GET</span>&nbsp;{urlHandler.API_ENDPOINT_V2_ENTITIES}/{'{'}knowledgebase{'}'}?{'{'}query parameters{'}'}
                </code>
                <div className="description">
                    Search the specified knowledgebase type for a particular name. The knowledgebase type will need to be given. 
                    The availabe types include:
                    <div className="bullet-list">
                        <ul className="toc-level list-wrapper">
                            <li className="list-item">PERSON</li>
                            <li className="list-item">ORGANIZATION</li>
                            <li className="list-item">THING</li>
                        </ul>
                    </div>               
                    <div>
                        <i>NOTE: To search for geographic entities, see <Link to="/documentation/doc_v1locationfinder">Location Finder</Link>.</i>
                    </div>
                </div>

                <div className="heading-3">URL Parameters</div>
                <div className="description">
                    The following URL parameters are supported for the search request.
                </div>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Notes </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">knowledgebase</td>
                        <td className="meaning">The Finch knowledgebase.
                        </td>
                        <td className="type">String</td>
                        <td className="type">Required</td>
                    </tr>
                    </tbody>
                </table>

                <div className="heading-3">Query Parameters</div>
                <div className="description">
                    The following query parameters are supported for the search request.
                </div>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Notes </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">query</td>
                        <td className="meaning">The name or string to search for.
                        </td>
                        <td className="type">String</td>
                        <td className="type">Required</td>
                    </tr>
                    <tr>
                        <td className="attribute">maxAnswers</td>
                        <td className="meaning">Maximum number of results to return.
                        </td>
                        <td className="type">Integer</td>
                        <td className="type">Optional. Default: 100</td>
                    </tr>
                    <tr>
                        <td className="attribute">select</td>
                        <td className="meaning">Comma separated list of fields to return in the response. 
                            The field names must be a valid JSON path for the element.
                        </td>
                        <td className="type">String</td>
                        <td className="type">Optional. Default: All fields<br/>
                            <br/>
                            For arrays, only the array[*] notation, to return all array elements, is supported at this time.
                        </td>
                    </tr>
                    </tbody>
                </table>
                
                <h4 className="heading-3">Response</h4>
                <div className="description">
                    The search request will return a JSON object of results. The object will contain the following fields.
                </div>

                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Notes </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">results</td>
                        <td className="meaning">Array of search results.
                        </td>
                        <td className="type">JSON array</td>
                        <td className="type">Array of <a href="#search_result_obj">Search Result Objects</a></td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-4" id="search_result_obj">Search Result Object</h4>
                <div className="description">
                    The search result is a JSON object that contains document information and a score.
                </div>

                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Notes </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">document</td>
                        <td className="meaning">JSON object of document fields. This is either all fields from the knowledgebase 
                            document or limited by those given via the select parameter.
                        </td>
                        <td className="type">JSON Object</td>
                        <td className="type">The entity 
                        object <Link to="/documentation/doc_kbattributes">fields</Link> available 
                        will vary by Knowledgebase entity type.
                        </td>
                    </tr>
                    <tr>
                        <td className="attribute">score</td>
                        <td className="meaning">Score of the document. A number that denotes its rank within the search results.
                        </td>
                        <td className="type">Float</td>
                        <td className="type">A number between 0 and 1 inclusive.</td>
                    </tr>
                    </tbody>
                </table>

                <div className="heading-3">Example</div>
                <div className="example-content host">
                    <pre>
                        GET {urlHandler.API_ENDPOINT_V2_ENTITIES}/PERSON?query=Donald+Trump&maxAnswers=10&select=id,label,urls[*]<br/>
                        Accept: application/json<br/>
                        Authorization: Bearer [YOUR-API-KEY]<br/>
                    </pre>
                </div>                
                <HttpRunner query="searchByName" 
                    response={!this.state.dynamicSampleSearch ? JSON.stringify(examples.EXAMPLE_RESPONSE_V2_ENTITIES4) : null}
                />

                <h4 className="heading-2">Retrieve KB Information</h4>
                <code className="api-definition">
                    <span className="get-badge">GET</span>&nbsp;{urlHandler.API_ENDPOINT_V2_ENTITIES}/{'{'}knowledgebase{'}'}
                </code>
                <div className="description">
                    Return statistics information about the specified knowlegebase. The availabe knowledgebases include:
                    <div className="bullet-list">
                        <ul className="toc-level list-wrapper">
                            <li className="list-item">PEOPLE</li>
                            <li className="list-item">ORGANIZATIONS</li>
                            <li className="list-item">THINGS</li>
                        </ul>
                    </div>                   
                </div>

                <div className="heading-3">URL Parameters</div>
                <div className="description">
                    The following URL parameters are supported for the search request.
                </div>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Notes </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">knowledgebase</td>
                        <td className="meaning">The name of the knowledgebase.
                        </td>
                        <td className="type">String</td>
                        <td className="type">Required</td>
                    </tr>
                    </tbody>
                </table>

                <h4 className="heading-3">Response</h4>
                <div className="description">
                    The information request will return a JSON object. The following table describes the fields.
                </div>
                <table className="table-wrapper enrich" id="information_object">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Notes </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">name</td>
                        <td className="meaning">Name of the knowledgebase.
                        </td>
                        <td className="type">String</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">recordCount</td>
                        <td className="meaning">Total number of records in the knowledgebase.
                        </td>
                        <td className="type">Integer</td>
                        <td className="type"></td>
                    </tr>
                    <tr>
                        <td className="attribute">inMemorySize</td>
                        <td className="meaning">The size of the knowledgebase in memory. Specified in megabytes (MB).
                        </td>
                        <td className="type">Integer</td>
                        <td className="type"></td>
                    </tr>
                    </tbody>
                </table>

                <div className="heading-3">Example</div>
                <div className="example-content host">
                    <pre>
                        GET {urlHandler.API_ENDPOINT_V2_ENTITIES}/PEOPLE<br/>
                        Accept: application/json<br/>
                        Authorization: Bearer [YOUR-API-KEY]<br/>
                    </pre>
                </div>                
                <HttpRunner query="kbInfo" 
                    response={!this.state.dynamicSampleSearch ? JSON.stringify(examples.EXAMPLE_RESPONSE_V2_ENTITIES5) : null}
                />

                <h4 className="heading-2">Retrieve All KB Information</h4>
                <code className="api-definition">
                    <span className="get-badge">GET</span>&nbsp;{urlHandler.API_ENDPOINT_V2_ENTITIES}/info
                </code>
                <div className="description">
                    Returns statistics information about all knowledgebases.              
                </div>

                <h4 className="heading-3">Response</h4>
                <div className="description">
                    The information request will return a JSON object. The following table describes the fields.
                </div>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Name </th>
                        <th>Description </th>
                        <th>Type </th>
                        <th>Notes </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">collections</td>
                        <td className="meaning">Array of JSON objects, each 
                            representing information for a knowledgebase.
                        </td>
                        <td className="type">JSON array</td>
                        <td className="type">Array of <a href="#information_object">JSON knowledgebase information</a> objects</td>
                    </tr>
                    </tbody>
                </table>

                <div className="heading-3">Example</div>
                <div className="example-content host">
                    <pre>
                        GET {urlHandler.API_ENDPOINT_V2_ENTITIES}/info<br/>
                        Accept: application/json<br/>
                        Authorization: Bearer [YOUR-API-KEY]<br/>
                    </pre>
                </div>                
                <HttpRunner query="kbInfoAll" 
                    response={!this.state.dynamicSampleSearch ? JSON.stringify(examples.EXAMPLE_RESPONSE_V2_ENTITIES6) : null}
                />

                <h4 className="heading-2">Error Responses</h4>
                <table className="table-wrapper enrich">
                    <thead className="table-header enrich">
                    <tr>
                        <th>Code </th>
                        <th>Status Text </th>
                        <th>Description </th>
                    </tr>
                    </thead>

                    <tbody className="table-body">
                    <tr>
                        <td className="attribute">400</td>
                        <td className="status-text">Bad Request</td>
                        <td className="notes">Invalid formatted entity id. Entity type cannot be decoded.</td>
                    </tr>
                    <tr>
                        <td className="attribute">404</td>
                        <td className="status-text">Not Found</td>
                        <td className="notes">The entity id cannot be found in the Knowledgebase.</td>
                    </tr>
                    </tbody>
                </table>
            </div>
        );
    }
}

export default DocV2Entities;